// RoomManageView.jsx
import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Typography,
  Paper,
} from "@mhome/ui";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddIcon from "@mui/icons-material/Add";
import {
  getRoomList,
  saveRoom,
  deleteRoom,
  moveRoom,
} from "../service/room-service";
import { useWebSocket } from "../ws/websocket";
import { useToast } from "../common/toast";
import LoadingSpinner from "../common/loading";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";

const RoomManageView = () => {
  const [roomList, setRoomList] = useState([]);
  const { sendAndWaitWs } = useWebSocket();
  const { successToast, errorToast } = useToast();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [editName, setEditName] = useState("");
  const [newRoomName, setNewRoomName] = useState("");
  const [loading, setLoading] = useState(false);
  const getComponentBgColor = useComponentBackgroundColor();

  useEffect(() => {
    handleListRoom();
  }, []);

  const handleListRoom = async () => {
    setLoading(true);
    try {
      const resp = await getRoomList(sendAndWaitWs);
      if (resp && resp.rooms) {
        setRoomList(resp.rooms);
      } else {
        errorToast("Failed to get room list");
      }
    } catch (error) {
      errorToast("Error loading room list");
      console.error("List room error:", error);
    }
    setLoading(false);
  };

  const handleCreateRoom = async () => {
    if (!newRoomName.trim()) {
      errorToast("Room name cannot be empty");
      return;
    }

    try {
      const response = await saveRoom(sendAndWaitWs, null, newRoomName.trim());
      if (response) {
        successToast("Room created successfully");
        await handleListRoom();
        setCreateDialogOpen(false);
        setNewRoomName("");
      } else {
        errorToast("Failed to create room");
      }
    } catch (error) {
      errorToast("Error creating room");
      console.error("Create room error:", error);
    }
  };

  const handleEditRoom = async () => {
    if (!editName.trim()) {
      errorToast("Room name cannot be empty");
      return;
    }

    try {
      const response = await saveRoom(
        sendAndWaitWs,
        selectedRoom.roomId,
        editName.trim()
      );
      if (response) {
        successToast("Room updated successfully");
        await handleListRoom();
        setEditDialogOpen(false);
      } else {
        errorToast("Failed to update room");
      }
    } catch (error) {
      errorToast("Error updating room");
      console.error("Update room error:", error);
    }
  };

  const handleDeleteRoom = async () => {
    try {
      const response = await deleteRoom(sendAndWaitWs, selectedRoom.roomId);
      if (response) {
        successToast("Room deleted successfully");
        await handleListRoom();
        setDeleteDialogOpen(false);
      } else {
        errorToast("Failed to delete room");
      }
    } catch (error) {
      errorToast("Error deleting room");
      console.error("Delete room error:", error);
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) return;

    const newList = Array.from(roomList);
    const [removed] = newList.splice(sourceIndex, 1);
    newList.splice(destinationIndex, 0, removed);
    setRoomList(newList);

    const newIndexes = {};
    newList.forEach((room, idx) => {
      newIndexes[room.roomId] = idx;
    });

    try {
      const response = await moveRoom(sendAndWaitWs, newIndexes);
      if (!response) {
        errorToast("Failed to reorder rooms");
        await handleListRoom();
      }
    } catch (error) {
      errorToast("Error reordering rooms");
      console.error("Reorder rooms error:", error);
      await handleListRoom();
    }
  };

  const handleEdit = (room) => {
    setSelectedRoom(room);
    setEditName(room.roomName);
    setEditDialogOpen(true);
  };

  const handleDelete = (room) => {
    setSelectedRoom(room);
    setDeleteDialogOpen(true);
  };

  return (
    <>
      <div className="py-4">
        <div className="mb-4">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            style={{ textTransform: "none", boxShadow: "none" }}
            size="small"
          >
            New Room
          </Button>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <DragDropContext
            onDragEnd={(result) => {
              document.body.style.overflow = "";
              onDragEnd(result);
            }}
            enableDefaultSensors={true}
            onDragStart={() => {
              document.body.style.overflow = "hidden";
            }}
          >
            <Droppable droppableId="room-manage-list">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="flex flex-col gap-3"
                >
                  {roomList.length > 0 ? (
                    roomList.map((room, index) => (
                      <Draggable
                        key={room.roomId}
                        draggableId={String(room.roomId)}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <Paper
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            elevation={snapshot.isDragging ? 5 : 0}
                            variant="outlined"
                            className={`p-3 flex items-center select-none ${
                              snapshot.isDragging
                                ? "cursor-grabbing"
                                : "cursor-grab"
                            } ${
                              snapshot.isDragging
                                ? ""
                                : "transition-colors duration-200"
                            }`}
                            style={{
                              ...provided.draggableProps.style,
                              backgroundColor: snapshot.isDragging
                                ? "hsl(var(--accent))"
                                : getComponentBgColor("hsl(var(--card))"),
                              WebkitTapHighlightColor: "transparent",
                              touchAction: snapshot.isDragging
                                ? "none"
                                : "manipulation",
                            }}
                            onMouseEnter={(e) => {
                              if (!snapshot.isDragging) {
                                e.currentTarget.style.backgroundColor =
                                  "hsl(var(--accent))";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!snapshot.isDragging) {
                                e.currentTarget.style.backgroundColor =
                                  getComponentBgColor("hsl(var(--card))");
                              }
                            }}
                          >
                            <div
                              {...provided.dragHandleProps}
                              className={`flex items-center mr-3 touch-none pointer-events-auto ${
                                snapshot.isDragging
                                  ? "cursor-grabbing"
                                  : "cursor-grab"
                              }`}
                              style={{
                                color: "hsl(var(--muted-foreground))",
                              }}
                            >
                              <DragIndicatorIcon />
                            </div>
                            <Typography variant="body1" className="flex-1">
                              {room.roomName}
                            </Typography>
                            <div
                              className="flex gap-1"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(room);
                                }}
                                style={{
                                  color: "hsl(var(--muted-foreground))",
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(room);
                                }}
                                style={{
                                  color: "hsl(var(--muted-foreground))",
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </div>
                          </Paper>
                        )}
                      </Draggable>
                    ))
                  ) : (
                    <Typography
                      color="textSecondary"
                      className="text-center py-8"
                    >
                      No rooms yet. Create one to get started.
                    </Typography>
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      {/* Create Room Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        variant="pops"
      >
        <DialogTitle>Create New Room</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Room Name"
            fullWidth
            value={newRoomName}
            onChange={(e) => setNewRoomName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleCreateRoom();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleCreateRoom} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Room Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        variant="pops"
      >
        <DialogTitle>Edit Room</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Room Name"
            fullWidth
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleEditRoom();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleEditRoom} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Room Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        variant="pops"
      >
        <DialogTitle>Delete Room</DialogTitle>
        <DialogContent>
          <Typography>
            {`Are you sure you want to delete "${selectedRoom?.roomName}"? Devices in this room will be moved to Unassigned.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteRoom}
            color="error"
            variant="contained"
            style={{ boxShadow: "none" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RoomManageView;
